import { View, Platform, ScrollView, StyleSheet } from "react-native";
import RText from "../RText";
import RHeading from "../RHeading/RHeading";
import variables from "../../styles/variables";
import { getStreamSessions } from "../../client/account";
import { useEffect, useState } from "react";
import Icon from "../Icon";
import RButton from "../Buttons/RButton";
import { RLink } from "../RLink/RLink";
import { FRONTEND_URL } from "../../client";
import Modal from "./AccessAndDevicesModal.tsx";
import { StreamSession } from "../../types/user";
import {
  formatTimeWithAmPm,
  getRelativeOrWeekdayWithOn,
} from "../../utils/TimeCalculator";
import { useResponsive } from "../../hooks/useResponsive";
import Gap from "../Gap";
import {
  deleteStreamSession,
  deleteAllStreamSessions,
} from "../../client/account";
import LoadingSpinner from "../LoadingSpinner";
import {
  openAuthModalAtom,
  isLoggedInAtom,
  refreshStreamCountAtom,
} from "../../atoms";
import { useAtom, useSetAtom } from "jotai";
import EmptyState from "../States/Empty/EmptyState";
import { ErrorState } from "../States/states";
import { toastError, toastSuccess } from "../../toasts/toasts";
import useDeviceType from "../../hooks/useDeviceType";
import { openDeviceManagementModalAtom } from "../../atoms";
import { logEvent } from "../../client/analytics/trackPageView";

/**
 * ManageAccessAndDevices component allows users to view and manage their active streaming sessions
 * Users can see a list of devices currently streaming on their account and remove any devices
 * either individually or all at once (except the current device)
 *
 * If the user is not logged in, they will be prompted to log in
 *
 * The component can be rendered either as a modal (i.e. on the Watch page)
 * or as a full screen view (i.e. in the account settings)
 *
 * @prop {boolean} isModal - Whether to render the component as a modal
 *
 * @returns {JSX.Element} The rendered ManageAccessAndDevices component.
 *
 */

const { colors } = variables;

const ManageAccessAndDevices = ({
  isModal,
  routeIsTab = false,
}: {
  isModal: boolean;
  routeIsTab?: boolean;
}) => {
  const [sessions, setSessions] = useState<StreamSession[]>([]);
  const [maxSessions, setMaxSessions] = useState<number>(0);
  const [visible, setVisibility] = useAtom(openDeviceManagementModalAtom);
  const setRefreshStreamCount = useSetAtom(refreshStreamCountAtom);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingSessionToken, setDeletingSessionToken] = useState<
    string | null
  >(null);

  const [isLoggedIn] = useAtom(isLoggedInAtom);
  const [, setIsAuthModalOpen] = useAtom(openAuthModalAtom);

  const { isMediumAndDown } = useResponsive();

  const { deviceType } = useDeviceType();
  const isTablet = deviceType === "TABLET";
  const isWeb = Platform.OS === "web";

  useEffect(() => {
    const fetchSessions = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getStreamSessions();
        setSessions(res.stream_sessions);
        setMaxSessions(res.max_sessions);
      } catch (err) {
        setError("Failed to load sessions. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (visible || routeIsTab) fetchSessions();
  }, [visible, isLoggedIn, routeIsTab]);

  const handleDeleteSession = async (sessionToken: string) => {
    setDeletingSessionToken(sessionToken);

    try {
      await deleteStreamSession(sessionToken);
      setSessions((prev) =>
        prev.filter((s) => s.session_token !== sessionToken)
      );
      toastSuccess("Device removed successfully");
    } catch (e) {
      toastError("Unable to remove this device. Please try again.");
    } finally {
      setDeletingSessionToken(null);
      logEvent({ key: "device-management-remove-pressed", data: {} });
    }
  };

  const handleClose = () => {
    setRefreshStreamCount((count) => count + 1);
    setVisibility(false);
    logEvent({ key: "device-management-modal-close", data: {} });
  };

  const handleDeleteAllSessions = async () => {
    setLoading(true);
    try {
      await deleteAllStreamSessions();
      setSessions((prev) => prev.filter((s) => s.is_current_device));
      toastSuccess("Devices removed successfully");
      handleClose();
    } catch (e) {
      toastError("Unable to remove devices. Please try again.");
    } finally {
      setLoading(false);
      logEvent({ key: "device-management-remove-all-pressed", data: {} });
    }
  };

  const Wrapper = isModal ? Modal : View;
  const wrapperProps = isModal ? { visible: visible, setVisibility } : {};

  if (!isLoggedIn && routeIsTab)
    return (
      <EmptyState
        message="You aren't logged in"
        iconType="person"
        component={
          <RButton
            onPress={() => setIsAuthModalOpen("login")}
            title="Login"
            dataSetButton={{ id: "preferences-login-button" }}
            dataSetButtonText={{ id: "preferences-login-button-text" }}
          />
        }
      />
    );

  if (loading) return <LoadingSpinner />;

  if (error && !sessions?.length) return <ErrorState />;

  const excessDevicesCount = (sessions?.length || 0) - maxSessions + 1;

  return (
    //@ts-ignore
    <Wrapper {...wrapperProps}>
      <View style={{ padding: isModal ? 20 : 0, width: "100%" }}>
        <View style={styles.topSection}>
          <View style={styles.flex}>
            {isModal ? (
              <RHeading level="1">Manage Access and Devices</RHeading>
            ) : null}

            <Gap vertical size="xsmall" />
            <RText size={isMediumAndDown ? "sm" : "md"}>
              These devices have recently been streaming on this account. You
              can remove any unfamiliar devices below, or
              <RLink
                size={isMediumAndDown ? "sm" : "md"}
                href={`${FRONTEND_URL}/my-account/reset-password`}
              >
                {" "}
                change your password
              </RLink>{" "}
              for added security.
            </RText>
            {excessDevicesCount > 0 && (
              <>
                <Gap vertical size="xsmall" />
                <RText
                  weight="semiBold"
                  size="md"
                  style={{ alignItems: "center", textAlign: "center" }}
                >
                  Please remove {excessDevicesCount} other device(s) to stream
                  on this device.
                </RText>
              </>
            )}
            <Gap vertical size="xsmall" />
          </View>
        </View>
        {!!sessions?.length && (
          <>
            <View
              style={{
                maxHeight: isModal
                  ? 400
                  : isMediumAndDown && !isTablet && !isWeb
                  ? 350
                  : undefined,
                marginBottom: 16,
              }}
            >
              <ScrollView
                nestedScrollEnabled
                showsVerticalScrollIndicator
                persistentScrollbar //works on android but not ios (there isn't a prop/easy fix for ios)
              >
                {!!sessions.length &&
                  sessions.map((session) => (
                    <DeviceBox
                      session={session}
                      key={session.session_token}
                      deletingSessionToken={deletingSessionToken}
                      handleDeleteSession={handleDeleteSession}
                    />
                  ))}
              </ScrollView>
            </View>
            <RButton
              title="Remove all other devices"
              onPress={handleDeleteAllSessions}
              disabled={sessions.every((s) => s.is_current_device)}
            />
          </>
        )}
        {!loading && sessions?.length === 0 && (
          <RText>No devices currently streaming on this account.</RText>
        )}
      </View>
    </Wrapper>
  );
};

const DeviceBox = ({
  session,
  deletingSessionToken,
  handleDeleteSession,
}: {
  session: StreamSession;
  deletingSessionToken: string | null;
  handleDeleteSession: (sessionToken: string) => void;
}) => {
  const { isSmallAndDown } = useResponsive();

  if (!session) return null;

  return (
    <>
      {session.is_current_device && <CurrentDevice />}
      <View
        style={[
          styles.deviceBox,
          {
            borderTopLeftRadius: session.is_current_device ? 0 : 5,
          },
        ]}
      >
        <View style={styles.flex}>
          <View style={styles.deviceBoxTextContainer}>
            {!!session.device_type && (
              <Icon
                name={session.device_type}
                size="xlarge"
                //@ts-ignore
                color={colors.palette.rtv.primary}
              />
            )}
            <View style={styles.flex}>
              <RText
                size={isSmallAndDown ? "sm" : "md_lg"}
                weight="bold"
                family="secondary"
              >
                {session.device_info}
              </RText>
              <RText color="grey">{`Streamed ${getRelativeOrWeekdayWithOn(
                new Date(session.created_at)
              )} at ${formatTimeWithAmPm(session.created_at)}`}</RText>
            </View>
          </View>
        </View>

        {deletingSessionToken === session.session_token ? (
          <LoadingSpinner
            style={{
              flex: 1,
              flexDirection: "row",
              alignSelf: "flex-end",
              justifyContent: "flex-end",
              paddingRight: 10,
            }}
          />
        ) : (
          <RButton
            title="Remove"
            onPress={() => handleDeleteSession(session.session_token)}
            //@ts-ignore
            color={!!deletingSessionToken ? "disabled" : "tertiary"}
            disabled={!!deletingSessionToken}
          />
        )}
      </View>
    </>
  );
};

const CurrentDevice = () => {
  return (
    <View
      style={[
        styles.currentDevice,
        {
          paddingBottom: Platform.OS === "android" ? 2 : 0,
        },
      ]}
    >
      <RText uppercase size="xs" weight="bold" color="white">
        Current device
      </RText>
    </View>
  );
};

const styles = StyleSheet.create({
  topSection: {
    marginBottom: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  flex: {
    flex: 4,
  },
  deviceBox: {
    borderWidth: 1,
    borderColor: colors.palette.neutral.x200,
    padding: 12,
    marginBottom: 12,
    borderTopRightRadius: 5,
    borderBottomLeftRadius: 5,
    borderBottomRightRadius: 5,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  deviceBoxTextContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
  },
  currentDevice: {
    backgroundColor: colors.palette.rtv.tertiary,
    alignSelf: "flex-start",
    paddingHorizontal: 5,
    borderTopLeftRadius: 3,
    borderTopRightRadius: 3,
  },
  inlineTitle: {
    marginBottom: 10,
    marginTop: 20,
  },
});

export default ManageAccessAndDevices;
